// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { AlertService } from '@app/core/services/alert.service';
import { Component, OnInit } from '@angular/core';
import { RolesFacadeService } from '@app/access-management/state/roles.facade';
import { Observable, of, zip } from 'rxjs';
import { readParameterFromRouterState, Role, RoleObject, RolesService, ScopeConstraint, UserService } from '@app/core';
import { ActivatedRoute } from '@angular/router';
import { UserDetailsFacadeService } from '@app/access-management/state/user-details/user-details.facade';
import { UserState } from '@app/access-management/state/user-details/user-details.interfaces';
import { map, switchMap } from 'rxjs/operators';
import { RoleScopeFacadeService } from '@app/access-management/state/role-scole.facade';
import { SelectedRows } from '@app/shared/data-table-ajax/data-table-ajax.component';
import { disableMultiSelectRadio, disableSelectAllRadio, isScopeAdmin } from '@app/core/utilities/scopes.helpers';

@Component({
  selector: 'app-user-details-roles-tab-add',
  templateUrl: './user-details-roles-tab-add.component.html',
  styleUrls: ['./user-details-roles-tab-add.component.scss']
})
export class UserDetailsRolesTabAddComponent implements OnInit {
  roles$: Observable<Role[]>;
  user$: Observable<UserState>;
  selectedRoles: Role[] = [];

  roleTableColumns = [
    {
      name: 'Role Name',
      prop: 'name',
      flexGrow: 2
    },
    {
      name: 'Users',
      prop: 'numberOfUsers',
      flexGrow: 1
    },
    {
      name: 'Description',
      prop: 'description',
      flexGrow: 2
    }
  ];

  constraintsTableColumns = [
    {
      prop: 'name',
      flexGrow: 2
    },
    {
      prop: 'IsParent',
      flexGrow: 2
    }
  ];

  categories: ScopeCategory[] = [];
  constrains$: Observable<any> = of();
  categoryRoleObjects: ScopeCategory[][] = [];
  categoryUrls: string[] = [];
  selectedCategories: {
    roleId: string;
    id: string;
    name: string;
    selectAll: boolean;
  }[] = [];
  selectedConstraints: any = [];
  selectedMultiScopeConstraints: any = [];
  currentUser: UserState | undefined;
  stepOne = true;
  stepTwo = false;
  username: string | undefined;
  userRoles$: any;
  categoryDefaultUrl = '/roles/scopes/admin';

  useProjectCommitteeHandler = false;

  userRolesv2$ = this.getRoles();
  selectedRole: any;

  constructor(
    private rolesFacade: RolesFacadeService,
    private userDetailsFacade: UserDetailsFacadeService,
    private roleService: RolesService,
    private roleScopeFacade: RoleScopeFacadeService,
    private alertService: AlertService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService
  ) {
    this.username = readParameterFromRouterState(this.activatedRoute, 'username');
    if (!this.username) {
      throw new Error('group id not in route');
    }

    this.userDetailsFacade.initialize(this.username);
    this.user$ = this.userDetailsFacade.user$;

    this.user$.subscribe(user => {
      this.currentUser = user;
    });

    this.rolesFacade.initialize();
    this.roles$ = this.userDetailsFacade.user$.pipe(
      switchMap(user =>
        this.rolesFacade.roles$.pipe(map((details: any) => this.excludeUserRolesFromRoleList(user, details.data)))
      )
    );

    this.roleScopeFacade.initializeCategories();
    this.roleScopeFacade.categories$.subscribe(data => (this.categories = data));

    this.userRoles$ = this.userService.getUserRoles(this.username);
  }

  getRoles() {
    return this.roleService.getList();
  }

  excludeUserRolesFromRoleList(user: UserState, roles: Role[]) {
    const userRoles = user.roles || [];
    if (!roles) {
      return [];
    }

    return roles.filter(role => !userRoles.find(userRole => userRole.id === role.id));
  }

  ngOnInit(): void {}

  onSelectRoles(rows: Role[]) {
    this.selectedRoles = rows;
  }

  onSelectRolev2(row: any) {
    this.onSelectRows({ selected: [row] });
  }

  onSelectRows(rows: SelectedRows) {
    if (rows.selected.length > 1) {
      this.alertService.error('Error: Can select only one role.');
      return;
    }

    if (rows.selected.length === 1) {
      this.selectedRoles = rows.selected;
      this.next();
      return;
    }

    this.selectedRoles = rows.selected;
  }

  addRoles() {
    const roles = this.selectedRoles;

    const requests = roles.map((role, index) =>
      this.roleService.addUsers(role.id, this.getRoleUserSelections(index) as any)
    );

    zip(...(requests as any), (...responses: any[]) => responses).subscribe(
      () => {
        this.userDetailsFacade.addUserRolesSucceed(this.currentUser);
      },
      err => {
        this.alertService.error('Error: There was an error adding roles to user');
      }
    );
  }

  selectCategory(
    category: ScopeCategory,
    roleId: string,
    categoryRef: any,
    roleObjectIndex: any,
    radioBtnRef: any,
    scopeCategoryRef: any,
    scopeMultipleRef: any,
    roleName: string,
    multiSelectRef: any,
    selectAllRef: any
  ) {
    if (category.name.toLowerCase() === 'project|committee') {
      this.useProjectCommitteeHandler = true;
      return;
    } else {
      this.useProjectCommitteeHandler = false;
    }

    const selectedCategory = {
      roleId: roleId,
      id: category.id,
      name: category.name,
      selectAll: false
    };
    this.selectedCategories[roleObjectIndex] = selectedCategory;

    if (category && roleId && categoryRef && radioBtnRef && scopeCategoryRef) {
      if (disableMultiSelectRadio(roleName, category.name)) {
        multiSelectRef.hidden = true;
        scopeCategoryRef.hidden = true;
        categoryRef.hidden = false;
        selectAllRef.hidden = false;
      } else if (roleId === categoryRef.id && !isScopeAdmin(category.name)) {
        multiSelectRef.hidden = false;
        radioBtnRef.checked = true;
        categoryRef.hidden = false;
        scopeCategoryRef.hidden = false;
        if (disableSelectAllRadio(roleName, category.name)) {
          selectAllRef.hidden = true;
        } else {
          selectAllRef.hidden = false;
        }
        if (category.name.toLowerCase() === 'project|organization') {
          this.categoryUrls[roleObjectIndex] = '/roles/scopes/1';
          scopeMultipleRef.hidden = false;
        } else {
          this.categoryUrls[roleObjectIndex] = '/roles/scopes/' + category.id;
          scopeMultipleRef.hidden = true;
        }
      } else {
        categoryRef.hidden = true;
      }
    }

    this.roleScopeFacade.initializeConstrains(category.id);
    this.constrains$ = this.roleScopeFacade.constrains$;
  }

  onSelectConstraints(row: SelectedRows, roleObjectIndex: any) {
    const category = this.selectedCategories[roleObjectIndex];
    if (category && category.name.toLowerCase() === 'organization' && row.selected.length > 1) {
      this.alertService.error('Error: Can select only one organization.');
    } else {
      this.selectedConstraints[roleObjectIndex] = row.selected;
    }
  }

  onSelectMultipleConstraints(row: SelectedRows, roleObjectIndex: any) {
    if (row.selected.length > 1) {
      this.alertService.error('Error: Can select only one organization.');
    } else {
      this.selectedMultiScopeConstraints[roleObjectIndex] = row.selected;
    }
  }

  next() {
    this.stepOne = false;
    this.stepTwo = true;
    this.resetSelection();
    this.selectedRoles.forEach((role, index) => {
      this.categoryRoleObjects.push([]);
      this.categoryUrls.push(this.categoryDefaultUrl);
      this.selectedConstraints.push();
      this.selectedCategories.push();
      this.selectedMultiScopeConstraints.push();
      this.roleService.getRoleObjects(role.id).subscribe(data => {
        if (data) {
          this.categoryRoleObjects[index] = this.includeObjectsFromRoleObjectsList(data);
        }
      });
    });
  }

  previous() {
    this.stepOne = true;
    this.stepTwo = false;
    this.resetSelection();
  }

  onSelectAll(radioBtnEvent: any, scopeCategoryRef: any, roleObjectIndex: any) {
    if (radioBtnEvent.target.checked) {
      scopeCategoryRef.hidden = true;
      const selectedCategory = this.selectedCategories[roleObjectIndex];
      if (selectedCategory) {
        this.selectedCategories[roleObjectIndex].selectAll = true;
      }
    }
  }

  onSelectMultiple(radioBtnEvent: any, scopeCategoryRef: any, roleObjectIndex: any) {
    if (radioBtnEvent.target.checked) {
      scopeCategoryRef.hidden = false;
      const selectedCategory = this.selectedCategories[roleObjectIndex];
      if (selectedCategory) {
        this.selectedCategories[roleObjectIndex].selectAll = false;
      }
    }
  }

  resetSelection() {
    this.categoryRoleObjects = [];
    this.categoryUrls = [];
    this.selectedConstraints = [];
    this.selectedCategories = [];
    this.selectedMultiScopeConstraints = [];
  }

  getRoleUserSelections(index: any) {
    let categoryId;
    let categoryName;
    const selectedCategory = this.selectedCategories[index];
    if (selectedCategory) {
      categoryId = selectedCategory.id;
      categoryName = selectedCategory.name;
      if (selectedCategory.selectAll) {
        this.selectedConstraints[index] = [];
        this.selectedConstraints[index] = [{ scopeId: '', id: '*', name: '' }];
      } else if (categoryName.toLowerCase() === 'project|organization') {
        if (this.selectedMultiScopeConstraints[index].length > 0) {
          const orgaConstraint = this.selectedMultiScopeConstraints[index][0];
          this.selectedConstraints[index].map((constraint: ScopeConstraint) => {
            constraint.id = constraint.id + '|' + orgaConstraint.id;
          });
        }
      }
    }
    return {
      users: [this.currentUser],
      category: {
        id: categoryId,
        name: categoryName
      },
      constraints: this.selectedConstraints[index]
    };
  }

  private includeObjectsFromRoleObjectsList(roleObjects: RoleObject[]) {
    return this.categories.filter(scope => roleObjects.find(object => object.object_id === scope.id));
  }
}

interface ScopeCategory {
  id: string;
  name: string;
}



// WEBPACK FOOTER //
// ./src/app/access-management/users/user-details/user-details-roles-tab-add/user-details-roles-tab-add.component.ts